import { useEffect } from "react";

import {
    Table,
    TableContainer,
    Thead,
    Tr,
    Th,
    Tbody,
    Td,
    Box,
    SkeletonText
} from "@chakra-ui/react";

import DeleteIcon from "@/assets/DeleteIcon";
import EditIcon from "@/assets/EditIcon";
import InactiveStatus from "@/assets/InactiveStatus";
import ActiveStatus from "@/assets/ActiveStatus";
import FileIcon from "@/assets/FileIcon";
import ButtonReview from "@/assets/ButtonReview";
import NewIcon from "@/assets/NewIcon";
import InReviewIcon from "@/assets/InReviewIcon";
import RejectIcon from "@/assets/RejectIcon";
import WaitingForReview from "@/assets/WaitingForReview";
import Pagination from "../Layouts/Pagination";
import { COLORS } from "@/common/constants";

const getTypes = (item: any) => {
    const result = [];

    if (item.sirebire) {
        result.push('SIRE')
    }

    if (item.vetting) {
        result.push('Vetting')
    }

    return result.join(', ');
}

const TableVessels = ({
    dataList,
    isLoading,
    handleEdit,
    handleDelete,
    handleReview,
    setSelectedOnBehalf,
    currentPageSize,
    handleChangePageSize,
    handleChangePageNumber,
    currentPageNumber,
    totalPageNumber,
    hasNextPage,
    role,
    handleDownload
}: any) => {
    useEffect(() => {
        if (dataList && dataList.length > 0) {
            setSelectedOnBehalf(dataList[0].onBehalfCompany);
        }
    }, [dataList, setSelectedOnBehalf]);

    const renderStatus = (status: number) => {
        switch (status) {
            case 0:
                return <p>-</p>;
            case 1:
                return <p>{role === 'super admin' ? <NewIcon /> : <WaitingForReview />}</p>;
            case 2:
                return <InReviewIcon />;
            case 3:
                return <ActiveStatus />;
            case 4:
                return <RejectIcon />;
            case 5:
                return <InactiveStatus />;
            default:
                return null;
        }
    };

    const renderActions = (item: any) => {
        if (role === 'super admin') {
            if (item.status === 0) {
                return <p>-</p>;
            }

            if ([1, 2, 4].includes(item.status)) {
                return <ButtonReview onClick={() => handleReview(item.id)} />
            }
        }

        if (role === 'ship operator') {
            if (item.status === 0 || item.status === 2) {
                return <p>-</p>;
            }
        }

        return (
            <>
                <EditIcon onClick={() => handleEdit(item.id)} />
                <DeleteIcon onClick={() => handleDelete(item.id)} />
            </>
        );
    };

    return (
        <TableContainer bgColor="#fff" borderRadius="13">
            <Box overflowX="auto">
                <Table variant='simple'>
                    <Thead>
                        <Tr>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Type</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>IMO / Register Number</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Vessel Name</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Company</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Company Email</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>PIC Name</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>PIC Email</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Contact Person</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>File SMC</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Status</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14}>Note</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" fontSize={14} textAlign='center'>Action</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {isLoading ? (
                            <Tr>
                                <Td colSpan={9}>
                                    <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='3' />
                                </Td>
                            </Tr>
                        ) : dataList.length === 0 ? (
                            <Tr>
                                <Td colSpan={9} textAlign="center">Data not found</Td>
                            </Tr>
                        ) : (
                            dataList.map((item: any) => (
                                <Tr key={item.id}>
                                    <Td>{getTypes(item) || '-'}</Td>
                                    <Td>{item.imoreg || '-'}</Td>
                                    <Td>{item.vesselName}</Td>
                                    <Td>{item.onBehalfCompany || '-'}</Td>
                                    <Td>{item.email || '-'}</Td>
                                    <Td>{item.picName || '-'}</Td>
                                    <Td>{item.picEmail || '-'}</Td>
                                    <Td>{item.contactPerson || '-'}</Td>
                                    <Td textAlign='center'>
                                        {item.smc &&
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <FileIcon style={{ cursor: 'pointer' }} onClick={() => handleDownload(item.smc)} />
                                            </div>
                                        }
                                        {!item.smc && <p>-</p>}
                                    </Td>
                                    <Td>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                                            {renderStatus(item.status)}
                                        </div>
                                    </Td>
                                    <Td>{item.note || '-'}</Td>
                                    <Td display="flex" justifyContent="center" gap={2} py={6} cursor='pointer'>
                                        {renderActions(item)}
                                    </Td>
                                </Tr>
                            ))
                        )}
                    </Tbody>
                </Table>
            </Box>
            <Pagination
                currentPageSize={currentPageSize}
                currentPageNumber={currentPageNumber}
                totalPageNumber={totalPageNumber}
                handleChangePageSize={handleChangePageSize}
                handleChangePageNumber={handleChangePageNumber}
                hasNextPage={hasNextPage}
            />
        </TableContainer>
    );
};

export default TableVessels;
